import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft } from "lucide-react";

import PageChrome from "@/layouts/PageChrome.jsx";
import { Card, CardBody, CardExtra, CardHead, CardTitle } from "@/components/ui/Card.jsx";
import Button from "@/components/ui/Button.jsx";
import { Callout } from "@/components/ui/Display.jsx";
import Pagination, { PaginationInfo } from "@/components/ui/Pagination.jsx";
import { BarChart } from "@/components/charts/index.jsx";
import { useConfirm, useToast } from "@/components/ui/feedback.js";

import UserHeader from "@/components/user-detail/UserHeader.jsx";
import HeroStats from "@/components/user-detail/HeroStats.jsx";
import {
    ActivityTimeline,
    SystemInfoCard,
    TimeDistribution,
    UsageList,
} from "@/components/user-detail/OverviewPanels.jsx";
import OverridesCard from "@/components/user-detail/OverridesCard.jsx";
import { IdleTimesTable, TimeEntriesTable } from "@/components/user-detail/EntryTables.jsx";
import {
    AppSessionsModal,
    IdleTimeDrawer,
    TimeEntryDrawer,
} from "@/components/user-detail/DetailOverlays.jsx";
import KeyLogList from "@/components/user-detail/KeyLogList.jsx";
import ShotCard from "@/components/screenshots/ShotCard.jsx";
import ScreenshotViewer from "@/components/screenshots/ScreenshotViewer.jsx";

import {
    idleTimeService,
    screenshotService,
    teamService,
    timeEntryService,
    usageService,
    userService,
} from "@/lib/api.js";
import { apiErrorMessage } from "@/lib/apiClient.js";
import { organizationQuery, latestDesktopVersionQuery } from "@/lib/queries.js";
import { useUrlRefresh } from "@/hooks/useUrlRefresh.js";
import { useAuth } from "@/context/auth.js";
import {
    dayjs,
    formatDate,
    formatHoursTick,
    formatMinutes,
    formatTime,
} from "@/lib/format.js";
import { downloadCsv } from "@/lib/csv.js";
import { niceHourMax } from "@/lib/chartTone.js";
import { flattenShots } from "@/lib/screenshots.js";
import {
    RANGE_PRESETS,
    distribution,
    featureGates,
    heroStats,
    idleRows,
    isOnline,
    timeEntryRows,
    timeline,
    totalOf,
    trendSeries,
    usageLists,
} from "@/lib/userDetail.js";

/*
 * Replaces frontend/app/components/activity/UserDetail.js and the eleven child
 * components around it.
 *
 * Five tabs over ONE date range. The range is the page's real state — every
 * panel is a view of the same window — so it lives in the URL alongside the tab
 * and both survive a reload and a shared link, which is what the old page's
 * ?page= parameter was reaching for.
 *
 * Six queries, all keyed on the range, none blocking another: a slow trend
 * request must not hold up the numbers above it.
 */
// The same sizes the Screenshots page offers, so the two read alike.
const SHOT_PAGE_SIZES = [25, 50, 100, 500];
const DEFAULT_SHOT_PAGE_SIZE = 100;

// The Key Log tab still fetches a range. See the query for why.
const KEYLOG_LIMIT = 500;
const NONE = [];

const TAB_KEYS = ["overview", "timeEntries", "idleTimes", "screenshots", "keyLog"];

export default function UserDetail() {
    const { id: userId } = useParams();
    const [params, setParams] = useSearchParams();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const toast = useToast();
    const confirm = useConfirm();
    const { user: me } = useAuth();

    /*
     * Both writes on this page are gated on the ADMIN role specifically, not on
     * the `manageUsers` capability. userController checks `req.user.role !==
     * "admin"` for both updateUser's settings block and deleteUser, so a
     * superAdmin — who has the capability — would be shown an enabled control
     * and then get a 403 from it.
     */
    const isAdmin = me?.role === "admin";
    const isSelf = me?.id === userId || me?._id === userId;

    // Latest published desktop version (catalog-driven, not a hardcoded constant).
    const { data: latestDesktop } = useQuery(latestDesktopVersionQuery());

    const tab = TAB_KEYS.includes(params.get("tab")) ? params.get("tab") : "overview";

    /*
     * A RANGE THE READER CAN PICK, not only the three presets.
     *
     * ?range=7d still works — it is what the dashboard links here with, and
     * what bookmarks hold — but an explicit ?from=/?to= wins over it, so
     * "the week of the incident" is expressible without counting backwards
     * from today. Same contract as the Screenshots page, so the two read alike.
     */
    const today = useMemo(() => dayjs().format("YYYY-MM-DD"), []);
    const preset = RANGE_PRESETS[params.get("range")] ? params.get("range") : "today";

    const range = useMemo(() => {
        const from = params.get("from");
        const to = params.get("to");

        if (from || to) {
            const a = from || to;
            const b = to || from;
            // Ends the wrong way round return nothing and look broken.
            return a <= b
                ? { startDate: a, endDate: b }
                : { startDate: b, endDate: a };
        }

        const end = dayjs();
        const start = end.subtract(RANGE_PRESETS[preset].days, "day");
        return { startDate: start.format("YYYY-MM-DD"), endDate: end.format("YYYY-MM-DD") };
    }, [params, preset]);

    /*
     * Writing a range clears ?range=, and choosing a preset clears the explicit
     * dates — otherwise the URL would carry two answers to the same question
     * and the reader could not tell which one the page used.
     */
    const setRange = (patch) => {
        const next = new URLSearchParams(params);

        // ?range= is an INPUT ONLY — the dashboard links here with it. Once a
        // range is chosen the URL carries the dates themselves, so it never
        // holds two descriptions of the same span.
        next.delete("range");
        for (const [key, value] of Object.entries(patch)) {
            if (value) next.set(key, value);
            else next.delete(key);
        }

        setParams(next);
        setShotPage(1);
    };

    const [shotPage, setShotPage] = useState(1);
    const [shotPageSize, setShotPageSize] = useState(DEFAULT_SHOT_PAGE_SIZE);
    const [viewerKey, setViewerKey] = useState(null);
    const [openEntry, setOpenEntry] = useState(null);
    const [openIdle, setOpenIdle] = useState(null);
    const [openApp, setOpenApp] = useState(null);
    const [keyLogMode, setKeyLogMode] = useState("keys");

    /*
     * Two values, on purpose. `keyLogSearch` follows the keyboard so typing
     * stays smooth; `keyLogQuery` lags it by 400ms and is what the request is
     * keyed on. getUserScreenshots does the filtering server-side, so a
     * per-keystroke refetch would be a request per character.
     */
    const [keyLogSearch, setKeyLogSearch] = useState("");
    const [keyLogQuery, setKeyLogQuery] = useState("");

    useEffect(() => {
        const id = window.setTimeout(() => setKeyLogQuery(keyLogSearch.trim()), 400);
        return () => window.clearTimeout(id);
    }, [keyLogSearch]);

    const setParam = (key, value) => {
        const next = new URLSearchParams(params);
        if (value && value !== "overview" && value !== "today") next.set(key, value);
        else next.delete(key);
        setParams(next, { replace: true });
    };

    /* ---- Queries ---- */

    const userQuery = useQuery({
        queryKey: ["user", userId],
        queryFn: () => userService.getUser(userId).then((r) => r.data?.data ?? {}),
    });

    const user = userQuery.data?.user ?? null;
    const systemInfo = userQuery.data?.systemInfo ?? null;
    const organizationId =
        typeof user?.organization === "object" ? user?.organization?._id : user?.organization;

    // The shared definition — see lib/queries.js for why this is not written
    // out per page any more.
    const orgQuery = useQuery(organizationQuery(organizationId));
    const organization = orgQuery.data?.organization;

    const teamsQuery = useQuery({
        queryKey: ["teams"],
        queryFn: () => teamService.getOrganizationTeams().then((r) => r.data?.data?.teams ?? []),
    });

    const [usageQuery, entriesQuery, idleQuery, trendQuery, shotsQuery, keyLogQueryResult] =
        useQueries({
        queries: [
            {
                queryKey: ["user-usage", userId, range],
                queryFn: () =>
                    usageService.getUserUsageStats(userId, range).then((r) => r.data?.data ?? {}),
            },
            {
                queryKey: ["user-entries", userId, range],
                queryFn: () =>
                    timeEntryService
                        .getUserTimeEntries(userId, range)
                        .then((r) => r.data?.data?.timeEntries ?? []),
            },
            {
                queryKey: ["user-idle", userId, range],
                queryFn: () =>
                    idleTimeService.getUserIdleTimes(userId, range).then((r) => r.data?.data ?? {}),
            },
            {
                queryKey: ["user-trend", userId, range],
                queryFn: () =>
                    timeEntryService
                        .getWorkHoursTrend({ ...range, userId })
                        .then((r) => r.data?.data ?? {}),
            },
            /*
             * TWO QUERIES, because the tabs ask different questions.
             *
             * They shared one — 500 records for the whole range, paged in the
             * browser — which capped the grid at whatever 500 records happened
             * to hold and gave the Key Log tab no paging at all. Sharing also
             * meant one page cursor between two lists: paging the grid moved
             * the key log, and the tab you were not looking at decided how much
             * you downloaded.
             */
            {
                // The grid: one page of captures, straight from the server.
                queryKey: ["user-shots", userId, range, shotPage, shotPageSize],
                queryFn: () =>
                    screenshotService
                        .getUserScreenshots(userId, {
                            ...range,
                            limit: shotPageSize,
                            page: shotPage,
                        })
                        .then((r) => ({
                            records: r.data?.data?.screenshots ?? [],
                            total: r.data?.total ?? 0,
                            summary: r.data?.summary ?? null,
                        })),
                placeholderData: (previous) => previous,
            },
            {
                /*
                 * The Key Log tab, with its own search. Still a range fetch
                 * rather than a page: its rows are the captures that HAVE key
                 * or clipboard entries, and the server cannot yet filter on
                 * that — so paging first would show "3 rows" from a page of a
                 * hundred and hide the rest. Bounded, and honest about the
                 * bound below.
                 */
                queryKey: ["user-keylog", userId, range, keyLogQuery],
                queryFn: () =>
                    screenshotService
                        .getUserScreenshots(userId, {
                            ...range,
                            limit: KEYLOG_LIMIT,
                            page: 1,
                            // Server-side, so the search covers the whole range
                            // rather than the rows that happened to be loaded.
                            ...(keyLogQuery ? { titleSearch: keyLogQuery } : {}),
                        })
                        .then((r) => ({
                            records: r.data?.data?.screenshots ?? [],
                            total: r.data?.total ?? 0,
                        })),
            },
        ],
    });

    /*
     * Bounded, for the reason in hooks/useUrlRefresh.js: a refetch re-signs
     * every URL, which clears ProxyImage's per-URL retry guard, so an
     * unbounded version loops forever on one missing object.
     */
    const refreshUrls = useUrlRefresh(
        useCallback(
            () =>
                queryClient.invalidateQueries({
                    queryKey: ["user-shots", userId, range, keyLogQuery],
                }),
            [queryClient, userId, range, keyLogQuery],
        ),
        `${userId}|${range.startDate}|${range.endDate}|${keyLogQuery}`,
    );

    /* ---- Derived ---- */

    // Its own memo: `?? {}` allocates a fresh object on every render, which
    // would change the deps of all three memos below it and defeat them.
    const summary = useMemo(() => usageQuery.data?.summary ?? {}, [usageQuery.data]);

    const stats = useMemo(
        () =>
            heroStats(summary, usageQuery.data?.usageStats ?? NONE, usageQuery.data?.browsers ?? NONE),
        [summary, usageQuery.data],
    );

    const dist = useMemo(() => distribution(summary), [summary]);
    const points = useMemo(() => timeline(summary), [summary]);
    const lists = useMemo(
        () =>
            usageLists({
                usageStats: usageQuery.data?.usageStats ?? NONE,
                browsers: usageQuery.data?.browsers ?? NONE,
            }),
        [usageQuery.data],
    );

    const entries = useMemo(() => timeEntryRows(entriesQuery.data ?? NONE), [entriesQuery.data]);
    const idles = useMemo(() => idleRows(idleQuery.data?.idleTimes ?? NONE), [idleQuery.data]);

    // Idle rows name the session they interrupted. The label is the entry's
    // start time, which is how a person recognises it on the previous tab.
    const entryLabels = useMemo(
        () => new Map(entries.map((e) => [e.id, formatTime(e.startTime)])),
        [entries],
    );

    const trend = useMemo(
        () => trendSeries(trendQuery.data?.trend ?? NONE, trendQuery.data?.granularity ?? "day"),
        [trendQuery.data],
    );

    const shots = useMemo(
        () =>
            flattenShots(shotsQuery.data?.records ?? NONE, {
                id: userId,
                name: user?.name ?? "",
                email: user?.email ?? "",
            }),
        [shotsQuery.data, userId, user],
    );

    /*
     * One row per CAPTURE for the Key Log tab: the key names and clipboard
     * entries belong to the interval, not to each monitor, so listing every
     * display would repeat the same keystrokes.
     *
     * From its OWN query — the grid's page would only show the key logs that
     * happen to be on the page you were last looking at.
     */
    const captures = useMemo(
        () =>
            flattenShots(keyLogQueryResult.data?.records ?? NONE, {
                id: userId,
                name: user?.name ?? "",
                email: user?.email ?? "",
            }).filter((s) => s.display === 1),
        [keyLogQueryResult.data, userId, user],
    );

    // Honest about the Key Log tab's bound: it fetches a range, not a page.
    const keyLogCapped = (keyLogQueryResult.data?.total ?? 0) > KEYLOG_LIMIT;

    const gates = useMemo(
        () => featureGates(organization?.settings, user?.settings, organization),
        [organization, user],
    );

    // Same five-minute rule the dashboard uses, so the two pages never
    // disagree about who is at their desk.
    const online = isOnline(summary.lastActive);

    /*
     * The page IS the response now, so there is nothing to slice. The totals
     * come from the server: `total` counts CAPTURES, which is what it pages by,
     * and `summary.images` counts the pictures those captures hold.
     */
    const shotTotal = shotsQuery.data?.total ?? 0;
    const shotSummary = shotsQuery.data?.summary ?? null;
    const shotTotalPages = Math.max(1, Math.ceil(shotTotal / shotPageSize));
    const shotCurrent = Math.min(shotPage, shotTotalPages);
    const visibleShots = shots;
    const viewerIndex = viewerKey ? visibleShots.findIndex((s) => s.key === viewerKey) : -1;

    /* ---- Mutations ---- */

    const saveOverrides = useMutation({
        mutationFn: (patch) => userService.updateUser(userId, { settings: patch }),
        onSuccess: () => {
            toast("Desktop overrides saved");
            queryClient.invalidateQueries({ queryKey: ["user", userId] });
        },
        onError: (error) => toast(apiErrorMessage(error, "Could not save the overrides."), "error"),
    });

    const removeUser = useMutation({
        mutationFn: () => userService.deleteUser(userId),
        onSuccess: () => {
            toast(`${user?.name} deleted`, "error");
            queryClient.invalidateQueries({ queryKey: ["users"] });
            navigate("/users");
        },
        onError: (error) => toast(apiErrorMessage(error, "Could not delete this user."), "error"),
    });

    const askDelete = async () => {
        const ok = await confirm({
            tone: "danger",
            title: `Delete ${user?.name}?`,
            body: "Their time entries, screenshots and activity are deleted with the account. This cannot be undone.",
            okLabel: "Delete user",
        });
        if (ok) removeUser.mutate();
    };

    /* ---- Export ---- */

    const exportCsv = () =>
        downloadCsv(
            `${user?.name?.replace(/\s+/g, "-").toLowerCase() ?? "user"}-${range.startDate}_to_${range.endDate}`,
            ["Date", "Start", "End", "Duration (h)", "Project", "Productivity %", "Ongoing"],
            entries.map((e) => [
                formatDate(e.startTime),
                formatTime(e.startTime),
                e.ongoing ? "" : formatTime(e.endTime),
                (e.duration / 3_600_000).toFixed(2),
                e.project ?? "",
                e.score == null ? "" : Math.round(e.score),
                e.ongoing ? "yes" : "no",
            ]),
        );

    /* ---- Render ---- */

    /*
     * Loading and error both keep the page's heading and its way back.
     * Rendering only a "Loading…" line leaves the route with no <h1> at all,
     * which gives a screen reader nothing to jump to and makes the two states
     * indistinguishable from a broken page.
     */
    if (userQuery.isError || !user) {
        return (
            <>
                <PageChrome
                    breadcrumb={[{ label: "Dashboard", to: "/dashboard" }, "User"]}
                    title="User"
                />
                <main className="mx-auto flex w-full max-w-[1680px] flex-col gap-5 p-4 lg:p-5">
                    <Link
                        to="/dashboard"
                        className="inline-flex w-fit items-center gap-1.5 text-[13px] font-medium text-slate-500 transition hover:text-brand-600 dark:text-slate-400 dark:hover:text-brand-400"
                    >
                        <ChevronLeft className="size-4" strokeWidth={2.2} />
                        Back to Dashboard
                    </Link>

                    <h1 className="text-xl font-semibold tracking-tight">User</h1>

                    {userQuery.isError ? (
                        <Callout tone="danger" data-error>
                            {apiErrorMessage(userQuery.error, "Could not load this user.")}
                        </Callout>
                    ) : (
                        <p className="text-[13px] text-slate-400 dark:text-slate-500">Loading…</p>
                    )}
                </main>
            </>
        );
    }

    const teamName =
        teamsQuery.data?.find((t) =>
            (t.members ?? []).some((m) => (typeof m === "object" ? m._id : m) === userId),
        )?.name ?? null;

    const tabs = [
        { value: "overview", label: "Overview" },
        { value: "timeEntries", label: "Time Entries", count: entries.length },
        { value: "idleTimes", label: "Idle Times", count: idles.length },
        { value: "screenshots", label: "Screenshots", count: shotTotal },
        { value: "keyLog", label: "Key Log" },
    ];

    return (
        <>
            <PageChrome
                breadcrumb={[{ label: "Dashboard", to: "/dashboard" }, user.name]}
                title={user.name}
            />

            <main className="mx-auto flex w-full max-w-[1680px] flex-col gap-5 p-4 lg:p-5">
                <Link
                    to="/dashboard"
                    className="inline-flex w-fit items-center gap-1.5 text-[13px] font-medium text-slate-500 transition hover:text-brand-600 dark:text-slate-400 dark:hover:text-brand-400"
                >
                    <ChevronLeft className="size-4" strokeWidth={2.2} />
                    Back to Dashboard
                </Link>

                <UserHeader
                    user={user}
                    systemInfo={systemInfo}
                    teamName={teamName}
                    lastActive={summary.lastActive}
                    online={online}
                    range={range}
                    onRange={(patch) => setRange(patch)}
                    maxDate={today}
                    tab={tab}
                    onTab={(value) => setParam("tab", value)}
                    tabs={tabs}
                    onExport={exportCsv}
                    exportDisabled={entries.length === 0}
                    // The API refuses both of these, so the button is not shown
                    // rather than shown and then rejected.
                    canDelete={isAdmin && !isSelf}
                    onDelete={askDelete}
                />

                {usageQuery.isError && (
                    <Callout tone="danger" data-usage-error>
                        {apiErrorMessage(usageQuery.error, "Could not load this user's activity.")}
                    </Callout>
                )}

                {tab === "overview" && (
                    <>
                        <HeroStats stats={stats} />

                        <section className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
                            <TimeDistribution distribution={dist} />
                            <ActivityTimeline points={points} />
                        </section>

                        <Card>
                            <CardHead>
                                <CardTitle
                                    hint={
                                        trendQuery.data?.granularity === "hour"
                                            ? "hourly"
                                            : "daily"
                                    }
                                >
                                    Work Hours Trend
                                </CardTitle>
                                <CardExtra>
                                    <span className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                                        <span className="size-2 rounded-sm bg-brand-500" /> Work hours
                                    </span>
                                </CardExtra>
                            </CardHead>
                            <CardBody>
                                {trend.length === 0 ? (
                                    <p className="py-10 text-center text-[13px] text-slate-400 dark:text-slate-500">
                                        No work hours recorded in this range.
                                    </p>
                                ) : (
                                    <BarChart
                                        data={trend}
                                        height={230}
                                        /*
                                         * The API reports HOURS. The axis gets
                                         * the short form — "0.25" is a number
                                         * nobody converts in their head — and
                                         * the tooltip gets hours AND minutes,
                                         * because "209m" is the same problem
                                         * one step along.
                                         */
                                        format={formatHoursTick}
                                        tooltipFormat={(v) => formatMinutes(v * 60)}
                                        niceMaxOf={niceHourMax}
                                    />
                                )}
                            </CardBody>
                        </Card>

                        <section className="grid grid-cols-1 gap-5 lg:grid-cols-3">
                            <UsageList
                                title="Used Applications"
                                list={lists.applications}
                                emptyHint="No application usage in this range."
                                onOpen={(row) => setOpenApp({ name: row.name, type: "application" })}
                            />
                            <UsageList
                                title="Used Web Browsers"
                                list={lists.browsers}
                                emptyHint="No browser usage in this range."
                                onOpen={(row) => setOpenApp({ name: row.name, type: "application" })}
                            />
                            <UsageList
                                title="Used Websites"
                                list={lists.websites}
                                emptyHint="No website usage in this range."
                                onOpen={(row) => setOpenApp({ name: row.name, type: "website" })}
                            />
                        </section>

                        <SystemInfoCard systemInfo={systemInfo} latestVersion={latestDesktop} />

                        <OverridesCard
                            settings={user.settings}
                            organization={organization}
                            canEdit={isAdmin}
                            saving={saveOverrides.isPending}
                            onSave={(patch) => saveOverrides.mutate(patch)}
                        />
                    </>
                )}

                {tab === "timeEntries" && (
                    <TimeEntriesTable
                        rows={entries}
                        total={totalOf(entries)}
                        onOpen={setOpenEntry}
                    />
                )}

                {tab === "idleTimes" && (
                    <IdleTimesTable
                        rows={idles}
                        total={idleQuery.data?.totalIdleDuration ?? totalOf(idles)}
                        entryLabels={entryLabels}
                        onOpen={setOpenIdle}
                    />
                )}

                {tab === "screenshots" && (
                    <Card>
                        <CardHead className="flex-wrap">
                            {/* A capture is a moment; a dual-monitor one holds
                                two pictures. Naming both explains why the grid
                                can show more cards than the page size. */}
                            <CardTitle
                                hint={[
                                    range.startDate === range.endDate
                                        ? formatDate(range.endDate)
                                        : `${formatDate(range.startDate)} – ${formatDate(range.endDate)}`,
                                    shotSummary && shotSummary.images !== shotSummary.records
                                        ? `${shotSummary.images} images across ${shotSummary.records} captures`
                                        : null,
                                ]
                                    .filter(Boolean)
                                    .join(" · ")}
                            >
                                Screenshots
                            </CardTitle>
                            <CardExtra>
                                <Button variant="link" to={`/screenshots?user=${userId}`}>
                                    Open full screenshot browser
                                </Button>
                            </CardExtra>
                        </CardHead>

                        {visibleShots.length === 0 ? (
                            <CardBody>
                                <p className="py-10 text-center text-[13px] text-slate-400 dark:text-slate-500">
                                    No screenshots in this range.
                                </p>
                            </CardBody>
                        ) : (
                            <div className="grid grid-cols-2 gap-3 p-4.5 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6">
                                {visibleShots.map((shot) => (
                                    <ShotCard
                                        key={shot.key}
                                        shot={shot}
                                        onOpen={() => setViewerKey(shot.key)}
                                        onExpired={refreshUrls}
                                    />
                                ))}
                            </div>
                        )}

                        <div className="flex flex-wrap items-center gap-3 border-t border-slate-200 px-4.5 py-3 dark:border-slate-800">
                            <PaginationInfo
                                page={shotCurrent}
                                pageSize={shotPageSize}
                                total={shotTotal}
                                sizes={SHOT_PAGE_SIZES}
                                noun="captures"
                                onPageSizeChange={(size) => {
                                    setShotPageSize(size);
                                    setShotPage(1);
                                }}
                            />
                            <div className="ml-auto">
                                <Pagination
                                    page={shotCurrent}
                                    totalPages={shotTotalPages}
                                    onChange={setShotPage}
                                />
                            </div>
                        </div>
                    </Card>
                )}

                {tab === "keyLog" && keyLogCapped && (
                    <Callout tone="warning" data-keylog-capped>
                        This range holds {keyLogQueryResult.data?.total} captures and the most
                        recent {KEYLOG_LIMIT} were searched. Narrow the dates, or use the search
                        box — it runs over the whole range.
                    </Callout>
                )}

                {tab === "keyLog" && (
                    <KeyLogList
                        shots={captures}
                        mode={keyLogMode}
                        onMode={setKeyLogMode}
                        search={keyLogSearch}
                        onSearch={setKeyLogSearch}
                        gates={gates}
                        subject={user.name}
                    />
                )}
            </main>

            {openEntry && (
                <TimeEntryDrawer entry={openEntry} onClose={() => setOpenEntry(null)} />
            )}

            {openIdle && (
                <IdleTimeDrawer
                    idle={openIdle}
                    entryLabel={entryLabels.get(openIdle.timeEntry)}
                    onClose={() => setOpenIdle(null)}
                />
            )}

            {openApp && (
                <AppSessionsModal
                    userId={userId}
                    target={openApp}
                    range={range}
                    onClose={() => setOpenApp(null)}
                />
            )}

            {viewerIndex >= 0 && (
                <ScreenshotViewer
                    shots={visibleShots}
                    index={viewerIndex}
                    // One user, so one resolved answer for every shot.
                    gatesFor={() => gates}
                    onIndexChange={(next) => {
                        const shot = visibleShots[next];
                        if (shot) setViewerKey(shot.key);
                    }}
                    onClose={() => setViewerKey(null)}
                    onExpired={refreshUrls}
                />
            )}
        </>
    );
}
